import { valibotResolver } from "@hookform/resolvers/valibot";
import { Link as LinkIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useTargetingMutations from "../../../../../datahooks/useTargetingMutations";
import { NavigatorUrlSchema } from "../../../../../schemas";

export default function UrlTargeting({ initialUrl }: { initialUrl: string }) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(!!initialUrl);
  const { setTargeting, isSettingTargeting } = useTargetingMutations();

  const {
    register,
    formState: { errors },
    setError,
    handleSubmit,
  } = useForm({
    defaultValues: {
      url: initialUrl || "",
    },
    resolver: valibotResolver(NavigatorUrlSchema),
  });

  function onSubmit({ url }: { url: string }) {
    setTargeting({ campaignTargeting: { url } }).catch((err) => {
      if (err instanceof HTTPError && err.data === "InvalidURL") {
        setError("url", { message: "Invalid URL" });
      }
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Trigger asChild>
        <Button size="sm" leftIcon={<LinkIcon />} variant="outline-black">
          Search by URL
        </Button>
      </Trigger>
      <Content size="lg">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Header title="Paste Sales Navigator Search URL" />
          <Body>
            <FormField
              size="lg"
              label="Sales Navigator Search URL"
              htmlFor="navigator-search-url"
              hint="Correct format starts with: 'https://www.linkedin.com/sales/search/people?query=...'"
              className="mb-8"
              error={errors.url?.message}
            >
              <Input
                id="navigator-search-url"
                variant="lg"
                placeholder="Enter URL here"
                {...register("url")}
              />
            </FormField>
          </Body>
          <Footer className="justify-end">
            <Button
              variant="secondary-black"
              onClick={() => setIsOpen(false)}
              disabled={isSettingTargeting}
            >
              Cancel
            </Button>

            <Button type="submit" isLoading={isSettingTargeting}>
              Next
            </Button>
          </Footer>
        </form>
      </Content>
    </Component>
  );
}
